import { useState } from 'react'
import { HttpVerb } from '../../../../../types/api-types'
import {
  WellEndPointFields,
  WellGeothermalPressureFields,
  WellGeothermalTemperatureFields,
  WellLogOrSurveyFields,
} from '../../../../../types/form-types'
import { FormDelegate, PageNavigation } from '../../../../../types/routing-types'
import ConfirmTransition from '../../../../components/confirm-transition'
import NotesWrapperEdit from '../../../../components/well-forms/notes/notes-wrapper-edit'
import WellAbandonmentEdit from '../../../../components/well-forms/well-geothermal/abandonment-edit'
import WellConditionEdit from '../../../../components/well-forms/well-geothermal/condition-of-well-edit'
import WellDocumentReferenceEdit from '../../../../components/well-forms/well-geothermal/document-references-edit'
import WellGeothermalDetailsEdit from '../../../../components/well-forms/well-geothermal/geothermal-details-edit'
import WellEndPointEdit from '../../../../components/well-forms/well-geothermal/well-end-point-edit'
import WellGeothermalPressureEdit from '../../../../components/well-forms/well-geothermal/well-geothermal-pressure-reading-edit'
import WellGeothermalTemperatureEdit from '../../../../components/well-forms/well-geothermal/well-geothermal-temperature-reading-edit'
import WellLogOrSurveyEdit from '../../../../components/well-forms/well-geothermal/well-log-or-survey-edit'
import EditRecordButtons from '../../../../components/wells/edit-record-buttons'
import { useGetWellData, useSaveWellData } from '../../../../hooks/use-api'
import useEditForm, { ValidationErrors } from '../../../../hooks/use-edit-form'
import useToastMessage from '../../../../hooks/use-toast-message'
import ViewWellGeothermal from '../../../../../app/routes/wells/[id]/view/geothermal'
import { useWellState } from '../../../../context/well-context'
import { ERROR_MESSAGES } from '../../../../components/errors/messages'
import { useWellId } from '../../../../hooks/use-well-id'
import {
  Divider,
  FieldTypes,
  Heading,
  RecordContent,
  RecordFooter,
  RepeatableSection,
  useTitleChange,
} from '@msaf/core-react'

export function EditWellGeothermal({ setIsDirty }: FormDelegate) {
  const wellId = useWellId()
  const {
    wellState: { isDraft },
  } = useWellState()
  useTitleChange(`${isDraft ? 'New' : 'Edit'} Well | Geothermal`)

  const urls: Omit<PageNavigation, 'saveAndReturnRoute'> = {
    requestUrl: `wells/${wellId}/geothermal/edit`,
    method: HttpVerb.PUT,
    saveAndContinueRoute: (id: string | number) => `/wells/${id}/edit/lithology`,
    cancelRoute: isDraft ? `/dashboard` : `/wells/${wellId}/view/geothermal`,
    cancelButtonLabel: isDraft ? 'Cancel' : 'Return to view',
  }

  const stateQueryResult = useGetWellData(urls.requestUrl)
  const saveMutationResult = useSaveWellData(urls.requestUrl)

  const {
    fields,
    isLoading,
    isSaveError,
    isSaved,
    error,
    setValue,
    handleSave,
    addField,
    removeField,
    editField,
    validationErrors,
  } = useEditForm({
    stateQueryResult,
    saveMutationResult,
    setIsDirty,
  })

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: 'Save successful',
    error,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  if (!isLoading && !fields.isGeothermalWell) {
    return <ViewWellGeothermal />
  }

  return (
    <>
      <RecordContent>
        <Heading level={2}>Geothermal</Heading>
        <WellGeothermalDetailsEdit
          isLoading={isLoading}
          fields={fields}
          setValue={setValue}
          validationErrors={validationErrors ?? []}
        />
        <Divider />
        <RepeatableSection
          heading='Well end points'
          sectionName='End point'
          removeField={(index: number) => removeField('wellEndPoints', index)}
          addField={() => addField('wellEndPoints', {})}
          isSkeleton={isLoading}
        >
          {fields.wellEndPoints?.map((wellEndPoint: Partial<WellEndPointFields>, index) => (
            <WellEndPointEdit
              key={wellEndPoint.id}
              fields={wellEndPoint}
              index={index}
              isLoading={isLoading}
              setValue={(key: keyof WellEndPointFields, val: FieldTypes) => setValue('wellEndPoints', val, key, index)}
              // FIXME-NWP - Correct types to avoid casting
              validationErrors={
                validationErrors.wellEndPoints
                  ? (validationErrors.wellEndPoints[index] as ValidationErrors<WellEndPointFields>)
                  : {}
              }
            />
          ))}
        </RepeatableSection>
        <Divider />
        <RepeatableSection
          heading='Well logs and survey'
          sectionName='Survey'
          removeField={(index: number) => removeField('wellLogOrSurveys', index)}
          addField={() => addField('wellLogOrSurveys', {})}
          isSkeleton={isLoading}
        >
          {fields.wellLogOrSurveys?.map((wellLogOrSurvey: Partial<WellLogOrSurveyFields>, index) => (
            <WellLogOrSurveyEdit
              key={wellLogOrSurvey.id}
              fields={wellLogOrSurvey}
              isLoading={isLoading}
              setValue={(key: keyof WellLogOrSurveyFields, val: FieldTypes) =>
                setValue('wellLogOrSurveys', val, key, index)
              }
              // FIXME-NWP - Correct types to avoid casting
              validationErrors={
                validationErrors.wellLogOrSurveys
                  ? (validationErrors.wellLogOrSurveys[index] as ValidationErrors<WellLogOrSurveyFields>)
                  : {}
              }
            />
          ))}
        </RepeatableSection>
        <Divider />
        <RepeatableSection
          heading='Pressure readings'
          sectionName='Reading'
          removeField={(index: number) => removeField('wellGeothermalPressureReadings', index)}
          addField={() => addField('wellGeothermalPressureReadings', {})}
          isSkeleton={isLoading}
        >
          {fields.wellGeothermalPressureReadings?.map(
            (wellGeothermalPressureReading: Partial<WellGeothermalPressureFields>, index) => (
              <WellGeothermalPressureEdit
                key={wellGeothermalPressureReading.id}
                fields={wellGeothermalPressureReading}
                index={index}
                isLoading={isLoading}
                setValue={(key: keyof WellGeothermalPressureFields, val: FieldTypes) =>
                  setValue('wellGeothermalPressureReadings', val, key, index)
                }
                // FIXME-NWP - Correct types to avoid casting
                validationErrors={
                  validationErrors.wellGeothermalPressureReadings
                    ? (validationErrors.wellGeothermalPressureReadings[
                        index
                      ] as ValidationErrors<WellGeothermalPressureFields>)
                    : {}
                }
              />
            ),
          )}
        </RepeatableSection>
        <RepeatableSection
          heading='Temperature readings'
          sectionName='Reading'
          removeField={(index: number) => removeField('wellGeothermalTemperatureReadings', index)}
          addField={() => addField('wellGeothermalTemperatureReadings', {})}
          isSkeleton={isLoading}
        >
          {fields.wellGeothermalTemperatureReadings?.map(
            (wellGeothermalTemperatureReading: Partial<WellGeothermalTemperatureFields>, index) => (
              <WellGeothermalTemperatureEdit
                key={wellGeothermalTemperatureReading.id}
                fields={wellGeothermalTemperatureReading}
                isLoading={isLoading}
                setValue={(key: keyof WellGeothermalTemperatureFields, val: FieldTypes) =>
                  setValue('wellGeothermalTemperatureReadings', val, key, index)
                }
                // FIXME-NWP - Correct types to avoid casting
                validationErrors={
                  validationErrors.wellGeothermalTemperatureReadings
                    ? (validationErrors.wellGeothermalTemperatureReadings[
                        index
                      ] as ValidationErrors<WellGeothermalTemperatureFields>)
                    : {}
                }
              />
            ),
          )}
        </RepeatableSection>
        <Divider />
        <WellConditionEdit
          isLoading={isLoading}
          fields={fields}
          setValue={setValue}
          validationErrors={validationErrors ?? []}
        />
        <Divider />
        <WellAbandonmentEdit
          isLoading={isLoading}
          fields={fields}
          setValue={setValue}
          validationErrors={validationErrors ?? []}
        />
        <Divider />
        <WellDocumentReferenceEdit
          isLoading={isLoading}
          fields={fields}
          setValue={setValue}
          validationErrors={validationErrors ?? []}
        />
      </RecordContent>
      <RecordFooter
        notes={
          <NotesWrapperEdit
            notes={fields.notes ?? []}
            isLoading={isLoading}
            addField={(note) => addField('notes', note, true)}
            removeField={(index: number) => removeField('notes', index)}
            editField={(note, idx) => editField('notes', note, idx)}
            setIsDirty={setIsDirty}
          />
        }
        areActionsFixed
      >
        <EditRecordButtons handleSave={handleSave} urls={urls} />
      </RecordFooter>
    </>
  )
}

export default function EditWellGeothermalWrapped() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <EditWellGeothermal setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
